import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormModal, Input, useToastActions } from '@airclou/ui';
import { updateCompany, type Company, type UpdateCompanyInput } from '@/api/companies';

interface EditCompanyModalProps {
  company: Company;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function EditCompanyModal({ company, isOpen, onClose, onSuccess }: EditCompanyModalProps) {
  const { t } = useTranslation('dashboard');
  const { success, error } = useToastActions();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<UpdateCompanyInput>({
    name: company.name,
    slug: company.slug,
  });

  const handleSubmit = async () => {
    setLoading(true);

    try {
      await updateCompany(company.id, formData);
      success(t('companies.companyUpdated'), t('common.success'));
      onSuccess();
      onClose();
    } catch (err: any) {
      error(err.message || t('companies.failedToUpdate'), t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = (formData.name?.trim().length ?? 0) > 0 && (formData.slug?.trim().length ?? 0) > 0;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('companies.editCompany')}
      size="md"
      submitText={loading ? t('common.saving') : t('common.save')}
      canSubmit={canSubmit && !loading}
      onSubmit={handleSubmit}
    >
      <div className="space-y-6">
        <div>
          <label htmlFor="edit-company-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('companies.companyName')}
          </label>
          <Input
            id="edit-company-name"
            type="text"
            value={formData.name || ''}
            onChange={(value) => setFormData({ ...formData, name: value })}
            placeholder="e.g., Engineering"
          />
        </div>

        <div>
          <label htmlFor="edit-company-slug" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('common.slug')}
          </label>
          <Input
            id="edit-company-slug"
            type="text"
            value={formData.slug || ''}
            onChange={(value) => setFormData({ ...formData, slug: value })}
            placeholder="e.g., engineering"
          />
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {t('common.slugHint')}
          </p>
        </div>
      </div>
    </FormModal>
  );
}
